import { useQuery } from '@apollo/client'

import { useState, useEffect, useMemo } from 'react'

import { Box, Flex, Toaster } from '@grupoboticario/flora-react'

import BenefitsToReceive from '@/components/BenefitsToReceive/BenefitsToReceive'
import ExtractDetailsWithCashback from '@/components/ExtractDetailsWithCashback/ExtractDetailsWithCashback'
import Filter from '@/components/Filter/Filter'
import InformationBox from '@/components/InformationBox/InformationBox'
import ExtractPointsTable from '@/components/Table/ExtractPointsTable/ExtractPointsTable'
import { useHeaderContext } from '@/context/header'
import GET_EXTRACT from '@/graphql/queries/getExtract'
import {
  EXTRACT_SUMMARY,
  getProgramFeatures
} from '@/utils/dictionaries/programFeaturesDictionary/programFeaturesDictionary'
import { extractPointsFilterSchema } from '@/utils/filterSchemas/extractPointsFilter'
import { businessUnit } from '@/utils/themes/theme'

import * as S from './ExtractPoints.styles'
import { CurrentMovement, FutureMovementRow } from './ExtractPoints.types'

const ExtractPoints = () => {
  const {
    member,
    memberStatus: { isBlocked }
  } = useHeaderContext()
  const { extractSummary } = getProgramFeatures(businessUnit(globalThis?.location?.hostname))
  const [filterState, setFilterState] = useState<CurrentMovement>({})

  const [data, setData] = useState<{ items: CurrentMovement[]; total: number }>({ items: [], total: 0 })
  const [futureEvents, setFutureEvents] = useState<{ items: FutureMovementRow[]; total: number }>({
    items: [],
    total: 0
  })
  const [pagination, setPagination] = useState({ from: 0, size: 10 })
  const [pageToken, setPageToken] = useState<string | null>(null)
  const [nextPage, setNextPage] = useState<string | null>(null)

  const filteredEvents = useMemo(() => {
    const events: CurrentMovement[] = data?.items || []
    const search = filterState.movementId?.toLowerCase()
    if (!search) return events

    return events.filter((item: CurrentMovement) => item.movementId?.toLowerCase().includes(search))
  }, [data?.items, filterState.movementId])

  useEffect(() => {
    setPagination((prev) => ({ from: 0, size: prev.size }))
  }, [filterState])

  const {
    loading,
    data: extractData,
    error,
    refetch,
    variables: queryVariables
  } = useQuery(GET_EXTRACT, {
    variables: {
      documentHash: member?.documentHash,
      includeFutureEvents: !pageToken,
      nextPage: pageToken
    },
    skip: !member?.documentHash
  })

  useEffect(() => {
    if (error) {
      setData({ items: [], total: 0 })
      setFutureEvents({ items: [], total: 0 })
      Toaster.notify({
        kind: 'error',
        description: 'Não foi possível carregar o extrato de pontos no momento.'
      })
      return
    }

    if (!extractData?.getExtract) return

    const { currentMovements, futureMovements, nextPage: responseNextPage } = extractData.getExtract
    const currentMovementsData = currentMovements || []

    setData((prev) => {
      const items = queryVariables?.nextPage ? [...prev.items, ...currentMovementsData] : currentMovementsData
      return { items, total: items.length }
    })

    if (!queryVariables?.nextPage) {
      setFutureEvents({ items: futureMovements || [], total: (futureMovements || []).length })
    }

    setNextPage(responseNextPage ?? null)
  }, [extractData, error, queryVariables])

  return (
    <Box css={S.topSpacerCss}>
      <Flex css={S.horizontalScrollCss} gap="$4">
        {extractSummary === EXTRACT_SUMMARY.CASHBACK ? (
          <ExtractDetailsWithCashback
            expirationDate={member?.lastPurchase?.expirationDate?.date}
            lastTransactionPoints={data?.items[0]?.amountPoint || 0}
            tier={member.tier}
            totalCashback={member?.cashback}
            totalLoyalty={member?.loyalty}
            totalPoint={member?.total}
          />
        ) : (
          <InformationBox
            expirationDate={member?.lastPurchase?.expirationDate?.date}
            isBlocked={isBlocked || false}
            lastTransactionPoints={data?.items[0]?.amountPoint || 0}
            tier={member.tier}
            totalPoint={member?.total?.point || 0}
          />
        )}
      </Flex>

      {!loading && !!futureEvents?.items.length && <BenefitsToReceive futureEvents={futureEvents?.items} />}

      <Filter
        content={extractPointsFilterSchema}
        css={S.filterSpacerCss}
        defaultKey="movementId"
        onChange={(filter: CurrentMovement) => setFilterState(filter)}
        placeholder="Busque pelo número do pedido"
        state={filterState}
        type="text"
      />
      <ExtractPointsTable
        css={S.tableSpacerCss}
        data={filteredEvents.slice(pagination.from, pagination.from + pagination.size)}
        total={filteredEvents.length}
        filter={filterState}
        loading={loading}
        onManualAdjustment={() => {
          setTimeout(() => {
            setPagination((prev) => ({ from: 0, size: prev.size }))
            setPageToken(null)
            setNextPage(null)
            refetch({ documentHash: member?.documentHash, includeFutureEvents: true, nextPage: null })
          }, 5000)
        }}
        onPaginationChange={({ from, size }) => {
          setPagination({ from, size })
          if (nextPage && from + size >= data.items.length) {
            setPageToken(nextPage)
          }
        }}
      />
    </Box>
  )
}

export default ExtractPoints
